import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Downtime = {
  propertyid: number
  downtimeid: number
}
// private module logic //

const id: string = "downtime"
const initialState: ApiResult<Downtime[]> = { id, state: "init", value: [] }
const downtimeStream = new BehaviorSubject<ApiResult<Downtime[]>>(initialState)
const downtimeEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let downtimeMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  downtimeStream.next({ id, state: 'ok', value: [] })
  downtimeMap = {}
}

function get(downtimeMapId: number): Downtime {
  return downtimeMap[downtimeMapId]
}

function refresh(): Promise<any> {
  const currentValue = downtimeStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchDowntime(propertyid)
}

function fetchDowntime(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Downtime[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  downtimeStream.next({ id, state: 'busy', value: downtimeStream.getValue().value })

  let url = `downtime?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `downtime?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Downtime[]) => {
    _.forEach(records, (downtime) => (downtimeMap[downtime.downtimeid] = downtime))
    // merge results unique by downtimeid
    downtimeStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<Downtime | { message: string }> {
  const currentValue = downtimeStream.getValue().value
  downtimeStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    inputtype: 'basic',
    downtimedetail: {
      state: 'basic'
    }
  }

  return Client.post(`downtime`, payload)
    .then((downtime: Downtime) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, downtime.downtimeid),
        redofunc: () => create(propertyid)
      })
      downtimeMap[downtime.downtimeid] = downtime
      // merge results unique by downtimeid
      const currentValue = downtimeStream.getValue().value
      downtimeStream.next({
        id,
        state: 'ok',
        value: _.unionBy([downtime], currentValue, 'downtimeid')
      })
      downtimeEventStream.next({ type: 'DOWNTIME_CREATED', payload: downtime })
      return downtime
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      downtimeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchDowntime(propertyid)
      return err
    })
}

function copy(propertyid:number, downtimeid:number, code:string) {
  const currentValue = downtimeStream.getValue().value;
  downtimeStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'downtime', recordid: downtimeid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((downtime: Downtime) => {
      const newRecord = downtime[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.downtimeid),
        redofunc: () => copy(propertyid, downtimeid, code)
      });
      downtimeMap[newRecord.downtimeid] = newRecord
      // merge results unique by downtimeid
      const currentValue = downtimeStream.getValue().value
      downtimeStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'downtimeid').sort((a, b) => alphanum(a.code, b.code))
      })
      downtimeEventStream.next({ type: 'DOWNTIME_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      downtimeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchDowntime(propertyid)
      return err
    })
}

function copies(propertyid:number, downtimeid:number, count: number) {
  const currentValue = downtimeStream.getValue().value;
  downtimeStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'downtime', recordid: downtimeid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((downtimes: Downtime[]) => {
      push_undo({
        undofunc: () => _.forEach(downtimes, newRecord => deleteOne(propertyid, newRecord.downtimeid)),
        redofunc: () => copies(propertyid, downtimeid, count)
      });
      _.forEach(downtimes, newRecord => downtimeMap[newRecord.downtimeid] = newRecord)
      // merge results unique by downtimeid
      const currentValue = downtimeStream.getValue().value
      downtimeStream.next({
        id,
        state: 'ok',
        value: _.unionBy(downtimes, currentValue, 'downtimeid').sort((a, b) => alphanum(a.code, b.code))
      })
      downtimeEventStream.next({ type: 'DOWNTIME_COPIED', payload: downtimes })
      return downtimes
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      downtimeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchDowntime(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  downtimeid: number,
): Promise<boolean | { message: string }> {
  const currentValue = downtimeStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, downtimeid)
  const refreshFn = () => fetchDowntime(propertyid)
  if (!(await handleReferences('downtime', downtimeid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted downtimeid
  const value = _(currentValue)
    .filter((p) => p.downtimeid !== downtimeid)
    .value()
  value.forEach((p) => (downtimeMap[p.downtimeid] = p))

  downtimeStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(downtimeid).then(() =>
  Client.del(`downtime?downtimeid=eq.${downtimeid}`)
    .then((downtime: Downtime) => {
      delete downtimeMap[downtime.downtimeid]
      push_undo({
        undofunc: () => {
          Client.post('downtime', downtime)
          downtimeMap[downtime.downtimeid] = downtime
          downtimeStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, downtimeid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      downtimeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchDowntime(propertyid)
      return err
    })
  )
}

function patch(downtime: Downtime): Promise<Downtime | { message: string }> {
  const currentValue = downtimeStream.getValue().value
  const downtimeid: number = downtime.downtimeid
  const currentDowntime = _.find(currentValue, { downtimeid })
  const changeset = diff_obj(downtime, currentDowntime)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentDowntime),
    redofunc: () => patch(downtime)
  })

  // optimistic update
  downtimeStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {downtimeid}), downtime)
  })

  return _patches_.watch(downtimeid, Client.patch(`downtime?downtimeid=eq.${downtimeid}`, changeset)
    .then((mf: Downtime[]) => {
      _.forEach(mf, (downtime) => (downtimeMap[downtime.downtimeid] = downtime))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      downtimeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentDowntime, 'propertyid')
      if (propertyid) fetchDowntime(propertyid)
      return err
    }))

}

// public module interface //

const downtimeService = {
  stream: () => downtimeStream.asObservable(),
  value: () => downtimeStream.getValue().value,
  eventStream: () => downtimeEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchDowntime,
  patch,
  refresh
}

export default downtimeService
